import { Button, Callout, Card, DropdownMenu, IconButton, PropertyList, PropertyListItem, Tag } from '@humanforce/mango-ui-react';
import type { PayRunApprovalDto, PayRunDetailDto } from './payRunsApiTypes';
import type { PayRunExceptionsPage } from './payRunPopulationApiTypes';
import { formatSystemValue } from './payRunsModel';

type TransitionVerb = 'make_payroll_active' | 'revert_to_trial' | 'unlock_to_compliance';

interface PayRunLifecyclePanelProps {
  payRun: PayRunDetailDto;
  approval: PayRunApprovalDto | null;
  exceptions: PayRunExceptionsPage | null;
  busy: boolean;
  approvalBusy: boolean;
  finalisationBusy: boolean;
  onTransition: (verb: TransitionVerb) => void;
  onCancel: () => void;
  onSubmitApproval: () => void;
  onRequestReadiness?: () => void;
  onFinalise: () => void;
}

const stageLabel = (status: string): string => ({
  open: 'Open',
  compliance: 'Compliance',
  ready_to_pay: 'Ready to Pay',
  eft_ready: 'EFT Ready',
  complete: 'Complete',
  cancelled: 'Cancelled',
}[status] ?? formatSystemValue(status));

const stageTone = (status: string): 'neutral' | 'pending' | 'positive' => {
  if (status === 'complete') return 'positive';
  if (status === 'open' || status === 'cancelled') return 'neutral';
  return 'pending';
};

const approvalCounts = (approval: PayRunApprovalDto) => {
  return {
    approved: approval.streams.reduce((total, stream) => total + Math.min(
      stream.requirements.filter((requirement) => !requirement.isSuperseded && requirement.status === 'approved').length,
      stream.requiredApprovalCount,
    ), 0),
    total: approval.streams.reduce((total, stream) => total + stream.requiredApprovalCount, 0),
  };
};

const streamProgress = (approval: PayRunApprovalDto, streamName: 'eft_disbursement' | 'super_disbursement') => {
  const stream = approval.streams.find((candidate) => candidate.approvalStream === streamName);
  if (!stream) return null;
  const approved = Math.min(
    stream.requirements.filter((requirement) => !requirement.isSuperseded && requirement.status === 'approved').length,
    stream.requiredApprovalCount,
  );
  return `${streamName === 'eft_disbursement' ? 'EFT' : 'Super'}: ${approved} of ${stream.requiredApprovalCount}`;
};

const when = (value: string): string => new Intl.DateTimeFormat('en-AU', {
  dateStyle: 'medium', timeStyle: 'short', timeZone: 'Australia/Sydney',
}).format(new Date(value));

export const PayRunLifecyclePanel = ({
  payRun,
  approval,
  exceptions,
  busy,
  approvalBusy,
  finalisationBusy,
  onTransition,
  onCancel,
  onSubmitApproval,
  onRequestReadiness,
  onFinalise,
}: PayRunLifecyclePanelProps) => {
  const lifecycleBusy = busy || approvalBusy || finalisationBusy;
  const projected = new Set(payRun.meta.actions);
  const secondaryActions = [
    ...(projected.has('revert_to_trial')
      ? [{ label: 'Revert to Trial', icon: 'refresh' as const, onClick: () => onTransition('revert_to_trial') }]
      : []),
    ...(projected.has('unlock_to_compliance')
      ? [{ label: 'Unlock to Compliance', icon: 'lock' as const, onClick: () => onTransition('unlock_to_compliance') }]
      : []),
    ...(projected.has('cancel')
      ? [{ label: 'Cancel Pay Run', icon: 'delete' as const, variant: 'destructive' as const, onClick: onCancel }]
      : []),
  ];
  const counts = approval ? approvalCounts(approval) : null;
  const finalisation = payRun.finalisation;
  const reconciled = finalisation?.legs.filter((leg) => leg.reconciled).length ?? 0;

  return (
    <section aria-labelledby="pay-run-lifecycle-heading">
      <Card
        header={(
          <div className="mgo-split mgo-gap-s mgo-align-center">
            <span id="pay-run-lifecycle-heading" className="mgo-text-l mgo-weight-strong">Pay Run lifecycle</span>
            <Tag label={stageLabel(payRun.workflowStatus)} tone={stageTone(payRun.workflowStatus)} />
          </div>
        )}
      >
        <div className="mgo-stack mgo-gap-l" aria-busy={lifecycleBusy}>
          <PropertyList orientation="vertical">
            <PropertyListItem label="Current stage">{stageLabel(payRun.workflowStatus)}</PropertyListItem>
            <PropertyListItem label="Operating state">{formatSystemValue(payRun.operatingState)}</PropertyListItem>
            {payRun.calculation && (
              <PropertyListItem label="Calculation">{formatSystemValue(payRun.calculation.status)}</PropertyListItem>
            )}
          </PropertyList>

          {payRun.workflowStatus === 'open' && (
            <Callout tone="info" title="Trial Pay">
              Inputs and calculation remain editable where your current permissions allow.
            </Callout>
          )}

          {payRun.workflowStatus === 'compliance' && (
            <Callout
              tone={(exceptions?.summary.blocking ?? 0) > 0 || exceptions?.readinessReason ? 'caution' : 'info'}
              title={(exceptions?.summary.blocking ?? 0) > 0 ? 'Compliance needs attention' : exceptions?.readinessReason ? 'Readiness blocked' : exceptions?.evaluationStatus === 'completed' ? 'Readiness prerequisite required' : 'Compliance evaluation required'}
              action={<Button label="View Compliance" size="small" variant="secondary" href={`/payroll/pay-runs/${payRun.payId}?tab=exceptions`} />}
            >
              {exceptions
                ? `${exceptions.summary.blocking} blocking, ${exceptions.summary.warnings} warnings and ${exceptions.summary.reviewRequired} review required. ${exceptions.readinessReason ? 'Review the readiness blocker above.' : 'Run readiness when the current prerequisites are satisfied.'}`
                : 'Compliance controls progression to Ready to Pay. Review current checks before continuing.'}
            </Callout>
          )}

          {payRun.workflowStatus === 'ready_to_pay' && (
            <div className="mgo-stack mgo-gap-m">
              <Callout tone="info" title={approval?.submittedAt ? 'Approval in progress' : 'Ready for approval'}>
                {approval && counts
                  ? `${counts.approved} of ${counts.total} approvals complete${approval.submittedAt ? '.' : '. Submit the frozen approval route when ready.'}`
                  : 'Approval details are loading. No approval action is available until the server projection is ready.'}
              </Callout>
              {approval?.submittedAt && (
                <div className="mgo-stack mgo-gap-xs" aria-label="Approval progress">
                  {streamProgress(approval, 'eft_disbursement') && <span className="mgo-text-s">{streamProgress(approval, 'eft_disbursement')}</span>}
                  {streamProgress(approval, 'super_disbursement') && <span className="mgo-text-s">{streamProgress(approval, 'super_disbursement')}</span>}
                </div>
              )}
            </div>
          )}

          {payRun.workflowStatus === 'eft_ready' && (
            <Callout tone="positive" title="Approvals complete">
              The existing EFT and Super approval requirements are complete. Finalise only when the server permits it.
            </Callout>
          )}

          {payRun.workflowStatus === 'complete' && (
            <div className="mgo-stack mgo-gap-m">
              <Callout tone={finalisation?.payRunFinalisedEventCreatedAt ? 'positive' : 'info'} title="Pay Run complete">
                There is no forward lifecycle action. Downstream failures do not undo finalisation.
              </Callout>
              {finalisation && (
                <PropertyList orientation="vertical">
                  <PropertyListItem label="Finalised">{when(finalisation.finalisedAt)} by {finalisation.finalisedBy}</PropertyListItem>
                  <PropertyListItem label="Downstream outputs">{reconciled} of {finalisation.legs.length} outputs reconciled</PropertyListItem>
                  <PropertyListItem label="PayRunFinalised">
                    <Tag label={finalisation.payRunFinalisedEventCreatedAt ? 'Recorded' : 'Pending'} tone={finalisation.payRunFinalisedEventCreatedAt ? 'positive' : 'pending'} />
                  </PropertyListItem>
                </PropertyList>
              )}
            </div>
          )}

          {payRun.workflowStatus === 'cancelled' && (
            <Callout tone="neutral" title="Pay Run cancelled">There is no lifecycle action available.</Callout>
          )}

          <div className="mgo-stack mgo-gap-s">
            {payRun.workflowStatus === 'open' && projected.has('make_payroll_active') && (
              <Button
                label="Make Payroll Active"
                icon="status-positive"
                fullWidth
                loading={busy}
                onClick={() => onTransition('make_payroll_active')}
              />
            )}
            {payRun.workflowStatus === 'compliance' && exceptions?.canRequestReadiness && (
              <Button label="Run readiness evaluation" fullWidth loading={busy} onClick={() => onRequestReadiness?.()} />
            )}
            {payRun.workflowStatus === 'ready_to_pay' && approval?.canSubmit && (
              <Button label="Submit for Approval" fullWidth loading={approvalBusy} disabled={busy} onClick={onSubmitApproval} />
            )}
            {payRun.workflowStatus === 'eft_ready' && projected.has('finalise') && (
              <Button label="Finalise Pay Run" icon="lock" fullWidth loading={finalisationBusy} disabled={busy} onClick={onFinalise} />
            )}
            {secondaryActions.length > 0 && (
              <DropdownMenu
                testId="pay-run-lifecycle-actions"
                disabled={lifecycleBusy}
                trigger={<IconButton icon="more" label="More lifecycle actions" variant="tertiary" disabled={lifecycleBusy} />}
                items={secondaryActions}
              />
            )}
          </div>
        </div>
      </Card>
    </section>
  );
};
